import {
  ConfirmDialog,
  CustomButton,
  CustomAgTable,
  DropdownType,
  SvgEntity,
  TabInterface,
  TableActionType,
  TableColumnType,
  tableFilterType,
  Tabs,
  Tooltip,
} from '@ui-kit'
import { PERMISSION_ASSIGNMENT } from '../../utils/strings'
import { isEmpty, noop } from 'lodash'
import {
  DataTypeStore,
  getPermissionAssignmentList,
  getPermissionAssignmentListForGlobal,
  getProgramsList,
  getUnassignedUsers,
  getUnassignedUsersForGlobal,
  getUserProfiles,
  hideLoader,
  setClientDropdown,
  showLoader,
  SORT_KEYS,
  TableFilterObjectBody,
  togglePermissionAssignment,
  updateAuthUserSelectedTenant,
} from '@app-store'
import { NAMESPACES, PERMISSION_MODULES } from '../../utils/constants'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { permissionAssignmentRoute } from '../../routes/route.constant'
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../utils/agent.store'
import {
  convertToCamelCase,
  formatFilterArray,
  permissionObject,
  programPermissionsObject,
} from '../../utils/helperFunctions'
import {
  ConfirmDialogWrapperProps,
  HCPProgramType,
} from '../../types/permissionAssignment.types'
import { isProgramAdmin, isSuperAdmin } from '../../utils/permission'
import { useFocusFirstElement } from '../../hooks/useFocusFirstFocusableElement'

const PermissionAssignmentList = ({ defaultTab }: { defaultTab?: string }) => {
  const location = useLocation()
  const programPermissions = useAppSelector(
    (state: RootState) => state.clientPermissions.programPermissions
  )
  const selectedTenantId = useAppSelector(
    (state: RootState) => state?.authUser?.selectedClientTenant?.id
  )
  const currUserId = useAppSelector(
    (state: RootState) => state?.authUser?.user?.user_id
  )
  const isLoading = useAppSelector((state: RootState) => state.loader.loading)
  const [tenantId, setTenantId] = useState<number | undefined>(
    selectedTenantId ?? -1
  )
  const [listData, setListData] = useState<DataTypeStore[]>([])
  const programIds = useRef<string[]>()
  const [hasDataFetched, setHasDataFetched] = useState(false)
  const [hasViewPermission, setHasViewPermission] = useState<boolean>(true)
  const [hasManagePermission, setHasManagePermission] = useState<boolean>()
  const [isDataFetched, setIsDataFetched] = useState(false)
  const initialModalData = {
    open: false,
    message: '',
    onConfirm: () => noop,
    onCancel: () => noop,
  }
  const [modal, setModal] = useState<{
    open: boolean
    message: string
    onConfirm: () => void
    onCancel: () => void
  }>(initialModalData)
  const [sortDirection, setSortDirection] = useState<{
    keyName: string
    direction: SORT_KEYS
    key?: string
  }>({
    keyName: 'firstName',
    direction: SORT_KEYS.Asc,
    key: 'first_name',
  })
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [apiFilter, setApiFilter] = useState<TableFilterObjectBody>({
    fields: null,
    search_criteria: {},
  })
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [rowPerPage, setRowPerPage] = useState<number>(25)
  const [totalPages, setTotalPages] = useState<number>(1)
  const [totalElements, setTotalElements] = useState<number>(1)
  const [numberOfElements, setNumberOfElements] = useState<number>(1)
  const [programOptions, setProgramOptions] = useState<
    DropdownType<DataTypeStore>[]
  >([])
  const [userProfiles, setUserProfiles] = useState<
    DropdownType<DataTypeStore>[]
  >([])
  const [userListOptions, setUserListOptions] = useState<
    {
      user_id: string
      id: number
      first_name: string
      last_name: string
    }[]
  >([])
  const paListContainerRef = useRef<HTMLDivElement | null>(null)
  const allTabs = [
    {
      label: 'Program Permissions',
      component: null,
      key: 'program',
    },
    {
      label: 'Global Permissions',
      component: null,
      key: 'global',
    },
  ]

  const [activeTab, setActiveTab] = useState<TabInterface>(
    defaultTab === 'Global Permissions' ? allTabs[1] : allTabs[0]
  )
  useFocusFirstElement(paListContainerRef)
  const columns: TableColumnType<DataTypeStore>[] = useMemo(
    () => [
      {
        header: PERMISSION_ASSIGNMENT.USER_ID,
        accessor: PERMISSION_ASSIGNMENT.USER_ID_KEY,
        type: 'hyperlink',
        isMandatory: true,
        filterData: {
          dataType: tableFilterType.DROPDOWN,
          dropDownOptions: userListOptions.map((item) => ({
            label: item.user_id,
            value: item.user_id,
          })),
        },
      },
      {
        header: PERMISSION_ASSIGNMENT.FIRST_NAME,
        accessor: PERMISSION_ASSIGNMENT.FIRST_NAME_KEY,
        isMandatory: true,
        isSortable: true,
        filterData: {
          dataType: tableFilterType.DROPDOWN,
          dropDownOptions: [
            ...new Map(
              userListOptions.map((user) => [
                user.first_name,
                {
                  label: user.first_name,
                  value: user.first_name,
                },
              ])
            ).values(),
          ],
        },
      },
      {
        header: PERMISSION_ASSIGNMENT.LAST_NAME,
        accessor: PERMISSION_ASSIGNMENT.LAST_NAME_KEY,
        isMandatory: true,
        isSortable: true,
        filterData: {
          dataType: tableFilterType.DROPDOWN,
          dropDownOptions: [
            ...new Map(
              userListOptions.map((user) => [
                user.last_name,
                {
                  label: user.last_name,
                  value: user.last_name,
                },
              ])
            ).values(),
          ],
        },
      },
      {
        header: PERMISSION_ASSIGNMENT.PROFILE,
        accessor: PERMISSION_ASSIGNMENT.PROFILE_KEY,
        isMandatory: true,
        isSortable: true,
        filterData: {
          dataType: tableFilterType.DROPDOWN,
          dropDownOptions: userProfiles,
        },
      },
      ...(activeTab?.label === 'Program Permissions'
        ? [
            {
              header: PERMISSION_ASSIGNMENT.PROGRAM,
              accessor: PERMISSION_ASSIGNMENT.PROGRAM_KEY,
              isMandatory: true,
              filterData: {
                dataType: tableFilterType.DROPDOWN,
                dropDownOptions: programOptions,
                isMultiSelect: true,
              },
            },
          ]
        : []),
    ],
    [programOptions, userListOptions, activeTab?.label, userProfiles]
  )

  const actions: TableActionType<DataTypeStore>[] = [
    {
      label: NAMESPACES.VIEW,
      key: NAMESPACES.VIEW_KEY,
      showInPopUp: false,
      isEnabledOnDisabledRow: true,
    },
    {
      label: NAMESPACES.EDIT,
      key: NAMESPACES.EDIT_KEY,
      showInPopUp: false,
    },
    {
      label: NAMESPACES.TOGGLE,
      key: NAMESPACES.TOGGLE,
      showInPopUp: false,
      isEnabledOnDisabledRow: true,
      type: NAMESPACES.TOGGLE,
      accessor: 'enabled',
    },
    {
      label: NAMESPACES.CLONE,
      key: NAMESPACES.CLONE_KEY,
      showInPopUp: true,
    },
  ]

  const handleToggle = async (userId: string) => {
    if (userId === undefined) return
    const enabled = listData.filter((user) => user.user_id === userId)[0]
      .enabled
    try {
      dispatch(showLoader())
      const response = await togglePermissionAssignment({
        id: userId,
        enabled: Boolean(!enabled),
        isGlobal: activeTab?.label === 'Global Permissions',
      })
      if (response) {
        fetchPermissionAssignmentListData(false, undefined, undefined)
      }
    } catch (err) {
      console.log(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const onFilterChange = (value: TableFilterObjectBody) => {
    setApiFilter(value)
    fetchPermissionAssignmentListData(true, undefined, value)
  }

  const onColumnClick = (
    _isHeader: boolean,
    columnKeyName: keyof DataTypeStore,
    rowData?: DataTypeStore | null,
    _rowIndex?: number | null
  ) => {
    if (columnKeyName === PERMISSION_ASSIGNMENT.USER_ID_KEY) {
      navigate(`/${permissionAssignmentRoute}/view/${rowData?.user_id}`)
    }
  }

  const onGlobalColumnClick = (
    _isHeader: boolean,
    columnKeyName: keyof DataTypeStore,
    rowData?: DataTypeStore | null,
    _rowIndex?: number | null
  ) => {
    if (columnKeyName === PERMISSION_ASSIGNMENT.USER_ID_KEY) {
      navigate(`/${permissionAssignmentRoute}/global/view/${rowData?.user_id}`)
    }
  }

  const onActionClick = (
    actionKeyName: string,
    rowData: DataTypeStore,
    _rowIndex: number
  ) => {
    switch (actionKeyName) {
      case NAMESPACES.VIEW_KEY:
        navigate(`/${permissionAssignmentRoute}/view/${rowData?.user_id}`)
        break
      case NAMESPACES.EDIT_KEY:
        navigate(`/${permissionAssignmentRoute}/edit/${rowData?.user_id}`)
        break
      case NAMESPACES.CLONE_KEY:
        navigate(`/${permissionAssignmentRoute}/clone/${rowData?.user_id}`)
        break
      case NAMESPACES.TOGGLE:
        handleToggle(rowData.user_id.toString())
        break
      default:
        break
    }
  }

  const onGlobalActionClick = (
    actionKeyName: string,
    rowData: DataTypeStore,
    _rowIndex: number
  ) => {
    switch (actionKeyName) {
      case NAMESPACES.VIEW_KEY:
        navigate(
          `/${permissionAssignmentRoute}/global/view/${rowData?.user_id}`
        )
        break
      case NAMESPACES.EDIT_KEY:
        navigate(
          `/${permissionAssignmentRoute}/global/edit/${rowData?.user_id}`
        )
        break
      case NAMESPACES.CLONE_KEY:
        navigate(
          `/${permissionAssignmentRoute}/global/clone/${rowData?.user_id}`
        )
        break
      case NAMESPACES.TOGGLE:
        handleToggle(rowData.user_id.toString())
        break
      default:
        break
    }
  }

  const addManageToActions = (row: DataTypeStore) => {
    if (isSuperAdmin()) {
      row['manage'] = true
      return
    }
    let managePermissionForSingleRow = false
    const programIds = String(row.program_id).split(',')
    for (const id of programIds) {
      if (
        permissionObject.Permission_Set_Assignment_Manage(
          programPermissions,
          PERMISSION_MODULES.PERMISSION_ASSIGNMENT,
          'Manage',
          id.toString()
        )
      ) {
        managePermissionForSingleRow = true
        break
      }
    }
    row['manage'] = managePermissionForSingleRow
  }

  const handlePageChange = async (page: number, rowsPerPage: number) => {
    try {
      if (page === currentPage && rowsPerPage === rowPerPage) return
      dispatch(showLoader())
      const selectedPage = rowPerPage !== rowsPerPage ? 1 : page
      const filterArray = formatFilterArray(apiFilter)
      filterArray.search_criteria.push({
        operator: 'CONTAINS_LIST',
        search_key: 'PROGRAM_ID',
        search_value: programIds.current ?? [],
      })
      filterArray.search_criteria.push({
        operator: 'NOT_EQUALS',
        search_key: 'EMAIL',
        search_value: [currUserId ?? ''],
      })
      if (activeTab?.label !== 'Global Permissions') {
        const response = await getPermissionAssignmentList({
          page: selectedPage,
          size: rowsPerPage,
          sort: !isEmpty(sortDirection?.keyName)
            ? `${sortDirection?.keyName},${sortDirection?.direction}`
            : undefined,
          filter: filterArray,
        })
        if (!isSuperAdmin()) {
          for (const index in response.data.content) {
            const row: DataTypeStore = response.data.content[Number(index)]
            addManageToActions(row)
          }
        }

        const programNames: Record<number, string> =
          await getProgramsNamesForList()

        const updatedResponse = response.data.content.map(
          (item: (typeof response.data.content)[0]) => ({
            ...item,
            disabled: !item.enabled,
            program_id: item.program_id
              .map((id: number) => {
                if (isSuperAdmin()) {
                  return programNames[id]
                }
                return programIds.current?.includes(id.toString())
                  ? programNames[id]
                  : undefined
              })
              .filter(Boolean)
              .join(', '),
          })
        )
        setListData(updatedResponse)
        setRowPerPage(rowsPerPage)
        setCurrentPage(selectedPage)
        setTotalPages(response?.data?.totalPages)
        setTotalElements(response?.data?.totalElements)
        setNumberOfElements(response?.data?.numberOfElements)
      } else {
        const response = await getPermissionAssignmentListForGlobal({
          page: selectedPage,
          size: rowsPerPage,
          sort: !isEmpty(sortDirection?.keyName)
            ? `${sortDirection?.keyName},${sortDirection?.direction}`
            : undefined,
          filter: filterArray,
        })
        const updatedResponse = response.data.content.map(
          (item: (typeof response.data.content)[0]) => ({
            ...item,
            disabled: !item?.enabled,
          })
        )
        setTotalPages(response?.data?.totalPages)
        setListData(updatedResponse)
        setRowPerPage(rowsPerPage)
        setCurrentPage(selectedPage)
        setTotalElements(response?.data?.totalElements)
        setNumberOfElements(response?.data?.numberOfElements)
      }
    } catch (err) {
      console.log(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const fetchPermissionAssignmentListData = async (
    resetPage: boolean,
    sortDir:
      | {
          keyName: string
          direction: SORT_KEYS
        }
      | undefined,
    filter: TableFilterObjectBody | undefined
  ) => {
    if (resetPage) setCurrentPage(1)
    const dir = sortDir ?? sortDirection
    try {
      dispatch(showLoader())
      const filterArray = formatFilterArray(filter ?? apiFilter)
      if (activeTab?.label !== 'Global Permissions') {
        filterArray.search_criteria.push({
          operator: 'CONTAINS_LIST',
          search_key: 'PROGRAM_ID',
          search_value: programIds.current ?? [],
        })
      }
      filterArray.search_criteria.push({
        operator: 'NOT_EQUALS',
        search_key: 'EMAIL',
        search_value: [currUserId ?? ''],
      })
      if (activeTab?.label !== 'Global Permissions') {
        const response = await getPermissionAssignmentList({
          page: resetPage ? 1 : currentPage,
          size: rowPerPage,
          sort: !isEmpty(dir?.keyName)
            ? `${dir?.keyName},${dir?.direction}`
            : undefined,
          filter: filterArray,
        })
        if (!isSuperAdmin()) {
          for (const index in response.data.content) {
            const row: DataTypeStore = response.data.content[Number(index)]
            addManageToActions(row)
          }
        }
        const programNames: Record<number, string> =
          await getProgramsNamesForList()

        const updatedResponse = response.data.content.map(
          (item: (typeof response.data.content)[0]) => ({
            ...item,
            disabled: !item.enabled,
            program_id: item.program_id
              .map((id: number) => {
                if (isSuperAdmin()) {
                  return programNames[id]
                }
                return programIds.current?.includes(id.toString())
                  ? programNames[id]
                  : undefined
              })
              .filter(Boolean)
              .join(', '),
          })
        )
        setTotalPages(response?.data?.totalPages)
        setTotalElements(response?.data?.totalElements)
        setNumberOfElements(response?.data?.numberOfElements)
        setListData(updatedResponse)
      } else {
        const response = await getPermissionAssignmentListForGlobal({
          page: resetPage ? 1 : currentPage,
          size: rowPerPage,
          sort: !isEmpty(dir?.keyName)
            ? `${dir?.keyName},${dir?.direction}`
            : undefined,
          filter: filterArray,
        })
        const updatedResponse = response.data.content.map(
          (item: (typeof response.data.content)[0]) => ({
            ...item,
            disabled: !item?.enabled,
          })
        )
        setTotalPages(response?.data?.totalPages)
        setTotalElements(response?.data?.totalElements)
        setNumberOfElements(response?.data?.numberOfElements)
        setListData(updatedResponse)
      }
    } catch (err) {
      console.log(err)
    } finally {
      dispatch(hideLoader())
      setIsDataFetched(true)
    }
  }

  const onSortClick = (key: string, isAsc: SORT_KEYS) => {
    if (key) {
      const newKeyName = convertToCamelCase(key)
      setSortDirection({
        keyName: isAsc === SORT_KEYS.Disabled ? '' : newKeyName,
        direction: isAsc,
        key: isAsc === SORT_KEYS.Disabled ? '' : key,
      })
      fetchPermissionAssignmentListData(
        false,
        {
          keyName: isAsc === SORT_KEYS.Disabled ? '' : newKeyName,
          direction: isAsc,
        },
        undefined
      )
    }
  }

  const getProgramsNamesForList = async () => {
    try {
      const programs = await getProgramsList()
      if (programs?.data?.content) {
        const programOptionsList: DropdownType<DataTypeStore>[] =
          programs?.data?.content
            ?.filter((item: HCPProgramType) => item.enabled)
            ?.map((item: HCPProgramType) => {
              if (!isSuperAdmin()) {
                if (programIds.current?.includes(item?.id?.toString() ?? '')) {
                  return {
                    label: item?.operational_program_name ?? '',
                    value: item?.id?.toString() ?? '',
                  }
                } else {
                  return undefined
                }
              } else {
                return {
                  label: item?.operational_program_name ?? '',
                  value: item?.id?.toString() ?? '',
                }
              }
            })
            .filter(Boolean)
        setProgramOptions([...programOptionsList])

        const programNames = programOptionsList.reduce<Record<string, string>>(
          (acc, programOption) => {
            acc[programOption.value] = programOption.label
            return acc
          },
          {}
        )
        return programNames
      } else {
        return []
      }
    } catch (err) {
      console.log(err)
      return []
    }
  }

  const fetchAllUsers = async () => {
    const programIdsWithPermission =
      programPermissionsObject.Permission_Set_Assignment_View(
        programPermissions,
        PERMISSION_MODULES.PERMISSION_ASSIGNMENT,
        'View Only'
      )
    const usersPromise =
      activeTab?.label !== 'Global Permissions'
        ? getUnassignedUsers(
            isSuperAdmin() ? [] : programIdsWithPermission,
            false
          )
        : getUnassignedUsersForGlobal(false)
    if (!userProfiles.length) {
      await Promise.all([usersPromise, fetchUserProfiles()])
    }
    const users = await usersPromise
    if (users?.data) {
      setUserListOptions([...users.data])
    }
  }

  const handleCancelClick = () => {
    setModal({
      open: false,
      message: '',
      onCancel: noop,
      onConfirm: noop,
    })
    dispatch(updateAuthUserSelectedTenant(tenantId ?? -1))
  }

  const handleConfirmClick = () => {
    setModal({
      open: false,
      message: '',
      onCancel: noop,
      onConfirm: noop,
    })
    setTenantId(selectedTenantId ?? -1)
    navigate('/')
  }

  const handleClientChange = () => {
    setApiFilter({
      fields: null,
      search_criteria: {},
    })
    setProgramOptions([])
  }

  const ProgramTable =
    listData.length ||
    Object.keys(apiFilter?.search_criteria ?? {}).length !== 0 ? (
      <div className="flex flex-1 min-h-0 min-w-0">
        <CustomAgTable<DataTypeStore>
          key={`program-table-${programOptions.length}`}
          tableName="permission-assignment-list-view-table"
          pageName="permission-assignment-list-view-page"
          columns={columns}
          data={listData ?? []}
          actions={actions}
          onActionClick={onActionClick}
          onColumnClick={onColumnClick}
          onFilterChange={onFilterChange}
          filterObject={apiFilter}
          sort={
            sortDirection.key && !isEmpty(sortDirection?.key)
              ? {
                  key: sortDirection?.key,
                  isAsc: sortDirection?.direction,
                }
              : null
          }
          onSortClick={onSortClick}
          disabledKey={'disabled'}
          onPageChange={handlePageChange}
          totalPages={totalPages ?? 0}
          totalElements={totalElements ?? 1}
          numberOfElements={numberOfElements ?? 1}
        />
      </div>
    ) : (
      <div className="my-[4.5rem] flex flex-col items-center gap-6">
        <SvgEntity />
        <div>{PERMISSION_ASSIGNMENT.NO_DATA_MESSAGE}</div>
      </div>
    )

  const AssignPermissionButton = (isGlobal: boolean) => (
    <Tooltip content={PERMISSION_ASSIGNMENT.ASSIGN_PERMISSION_BTN_TOOLTIP}>
      <CustomButton
        isFilled={true}
        dataQaId="permission-assignment-assign-permissions-btn"
        widthClass="w-auto"
        onClick={() => {
          navigate(
            `/${permissionAssignmentRoute}/assign-permissions${
              isGlobal ? '/global' : ''
            }`
          )
        }}
      >
        <span className="font-primary font-light text-xs">
          {PERMISSION_ASSIGNMENT.ASSIGN_PERMISSIONS}
        </span>
      </CustomButton>
    </Tooltip>
  )
  const fetchUserProfiles = async () => {
    try {
      dispatch(showLoader())
      const response = await getUserProfiles()
      if (response.data) {
        const profiles = response.data.map(
          (profile: { id: string; profile: string }) => ({
            label: profile.profile,
            value: profile.profile,
          })
        )
        setUserProfiles(profiles)
      }
    } catch (err) {
      console.log(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const fetchData = async () => {
    await fetchPermissionAssignmentListData(false, undefined, undefined)
    await fetchAllUsers()
    setHasDataFetched(true)
  }

  useEffect(() => {
    if (!activeTab) return
    if (!isSuperAdmin() && Object.keys(programPermissions).length === 0) return
    const viewPermission = permissionObject.Permission_Set_Assignment_View(
      programPermissions,
      PERMISSION_MODULES.PERMISSION_ASSIGNMENT,
      'View Only'
    )
    const managePermission = permissionObject.Permission_Set_Assignment_View(
      programPermissions,
      PERMISSION_MODULES.PERMISSION_ASSIGNMENT,
      'Manage'
    )
    setHasManagePermission(managePermission)
    setHasViewPermission(viewPermission)
    const programIdsWithPermission =
      programPermissionsObject.Permission_Set_Assignment_View(
        programPermissions,
        PERMISSION_MODULES.PERMISSION_ASSIGNMENT,
        'View Only'
      )
    programIds.current = programIdsWithPermission
    if (viewPermission || isProgramAdmin()) {
      fetchData()
    } else {
      setModal({
        open: true,
        message: PERMISSION_ASSIGNMENT.CONFIRM_DIALOG_MESSAGE,
        onConfirm: handleConfirmClick,
        onCancel: handleCancelClick,
      })
    }
  }, [programPermissions, activeTab])

  useEffect(() => {
    dispatch(
      setClientDropdown({
        isVisible: true,
        onChange: handleClientChange,
        isGlobalVisible: false,
      })
    )
  }, [location])

  if (!isSuperAdmin() && Object.keys(programPermissions).length === 0)
    return null

  return (
    <div className="flex flex-col h-full" ref={paListContainerRef}>
      <div className="flex justify-between gap-2 items-center p-3 border-b-2">
        <div className="flex">
          <span className="text-base font-primary font-medium">
            {PERMISSION_ASSIGNMENT.PERMISSION_ASSIGNMENT}
          </span>
        </div>
        <div className="inline-flex items-center gap-2">
          {!isSuperAdmin() && !isProgramAdmin() && hasManagePermission ? (
            <>{AssignPermissionButton(false)}</>
          ) : null}
        </div>
      </div>
      {(isSuperAdmin() || isProgramAdmin()) && hasDataFetched ? (
        <>
          <div className="flex mt-2 pr-3 items-center pb-2">
            <Tabs
              tabsWrapperClassName="flex-1 min-h-0"
              onChange={(_label: string, index: number) => {
                const selectedTab = allTabs[index]
                setActiveTab(allTabs[index])
                if (selectedTab.label === 'Global Permissions') {
                  navigate(`/${permissionAssignmentRoute}/global`)
                } else {
                  navigate(`/${permissionAssignmentRoute}`)
                }
              }}
              tabs={allTabs}
              defaultActive={activeTab}
            />
            {hasManagePermission ? (
              <div className="flex justify-end">
                {AssignPermissionButton(
                  activeTab?.label === 'Global Permissions'
                )}
              </div>
            ) : null}
          </div>
          {activeTab?.label === 'Global Permissions' ? (
            <div
              className={`${
                listData.length ? 'flex-1 min-h-0 flex flex-col' : ''
              }`}
            >
              {listData.length ||
              Object.keys(apiFilter?.search_criteria ?? {}).length !== 0 ? (
                <div className="flex flex-1 min-h-0 min-w-0">
                  <CustomAgTable<DataTypeStore>
                    key={`global-table-${programOptions.length}`}
                    tableName="permission-assignment-list-view-table-global"
                    pageName="permission-assignment-list-view-page-global"
                    columns={columns}
                    data={listData ?? []}
                    actions={actions}
                    onActionClick={onGlobalActionClick}
                    onColumnClick={onGlobalColumnClick}
                    onFilterChange={onFilterChange}
                    filterObject={apiFilter}
                    sort={
                      sortDirection.key && !isEmpty(sortDirection?.key)
                        ? {
                            key: sortDirection?.key,
                            isAsc: sortDirection?.direction,
                          }
                        : null
                    }
                    onSortClick={onSortClick}
                    disabledKey={'disabled'}
                    onPageChange={handlePageChange}
                    totalPages={totalPages ?? 0}
                    totalElements={totalElements ?? 1}
                    numberOfElements={numberOfElements ?? 1}
                  />
                </div>
              ) : (
                <div className="my-[4.5rem] flex flex-col items-center gap-6">
                  <SvgEntity />
                  <div>{PERMISSION_ASSIGNMENT.NO_DATA_MESSAGE}</div>
                </div>
              )}
            </div>
          ) : (
            <div
              className={`${
                listData.length ? 'flex-1 min-h-0 flex flex-col' : ''
              }`}
            >
              {ProgramTable}
            </div>
          )}
        </>
      ) : hasViewPermission && hasDataFetched ? (
        <div
          className={`${listData.length ? 'flex-1 min-h-0 flex flex-col' : ''}`}
        >
          {ProgramTable}
        </div>
      ) : (
        <ConfirmDialogWrapper
          isDataFetched={isDataFetched}
          isLoading={isLoading}
          modal={modal}
        />
      )}
    </div>
  )
}

const ConfirmDialogWrapper: React.FC<ConfirmDialogWrapperProps> = ({
  isDataFetched,
  isLoading,
  modal,
}) => {
  if (isDataFetched && !isLoading) {
    return (
      <ConfirmDialog
        open={modal.open}
        message={modal.message}
        onCancel={modal.onCancel}
        onConfirm={modal.onConfirm}
        confirmButtonText={'Back to Home'}
        confirmButtonWidth={'w-[8rem]'}
      />
    )
  }
  return null
}

export default PermissionAssignmentList
